import { Type, Transform } from "class-transformer";
import {
  IsNotEmpty,
  IsEnum,
  IsUUID,
  ValidateNested,
  IsEmail,
  IsPhoneNumber,
  IsString,
  IsISO31661Alpha2,
  IsOptional,
  Length,
  IsBIC,
  IsIBAN,
  MaxLength,
  IsNumber,
  IsPostalCode,
  ValidateIf,
  Matches,
} from "class-validator";
import { t as i18nValidationMessage } from "i18next";

import {
  ApplicationStateApplicationType,
  CounterPartyAccountFIATUSType,
  CounterPartyCounterpartyType,
  CounterPartyProfileFIATUSLineOfBusiness,
  CounterPartyProfileFIATUSRelationshipToCustomer,
  DepositDepositType,
  SupportedRails,
  CounterPartyIntermediaryIntermediaryType,
  WithdrawalPurpose,
  CounterPartyAccountCRYPTOWalletType,
  WithdrawalType,
  BridgeAccountOwnerType,
  BridgeBankAccountType,
} from "@/utils/dto/extracted_enums";

import "@/locales/i18n";

export enum AccountType {
  Fiat = "FIAT",
  Crypto = "CRYPTO",
}

export enum AccountAsset {
  USD = "USD",
  EUR = "EUR",
  BTC = "BTC",
  ETH = "ETH",
  USDC = "USDC",
}

export class OpenAccountRequest {
  @IsEnum(AccountType, {
    message: i18nValidationMessage("validation:INVALID_ENUM"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  type: AccountType;

  @IsEnum(AccountAsset, {
    message: i18nValidationMessage("validation:INVALID_ENUM"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  asset: AccountAsset;
}

export class CreateDepositRequest {
  @IsEnum(DepositDepositType, {
    message: i18nValidationMessage("validation:INVALID_ENUM"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  type: DepositDepositType;

  @IsUUID("4", { message: i18nValidationMessage("validation:INVALID_UUID") })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  accountId: string;
}

//To FEDWIRE and SWIFT transactions
export class CounterpartyAddressRequest {
  @IsOptional()
  @IsString()
  @Transform(({ value }) => (value === "" ? null : value))
  unitNumber?: string;

  @IsString()
  @MaxLength(35)
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  addressLine1?: string;

  @IsOptional()
  @IsString()
  @MaxLength(35)
  @Transform(({ value }) => (value === "" ? null : value))
  addressLine2?: string;

  @IsOptional()
  @IsString()
  @MaxLength(35)
  @Transform(({ value }) => (value === "" ? null : value))
  addressLine3?: string;

  @IsString()
  @MaxLength(21)
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  city?: string;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  state?: string;

  @IsISO31661Alpha2({
    message: i18nValidationMessage("validation:COUNTRY_FORMAT"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  countryCode: string;

  @ValidateIf((object) => object.countryCode !== "US")
  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  postalCode?: string;

  @ValidateIf((object) => object.countryCode === "US")
  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsPostalCode("US", {
    message: i18nValidationMessage("validation:INVALID_ZIP"),
  })
  postalCodeUs?: string;
}

export class ExternalAccountRequest {
  @Matches(/^\S+(\s+\S+)+$/, {
    message: i18nValidationMessage("validation:FULL_NAME_REQUIRED"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  beneficiaryName: string;

  @IsEnum(BridgeAccountOwnerType, {
    message: i18nValidationMessage("validation:INVALID_ENUM"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  accountOwnerType: string;

  @IsEnum(BridgeBankAccountType, {
    message: i18nValidationMessage("validation:INVALID_ENUM"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  accountType: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsString()
  accountNumber: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsString()
  routingNumber: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsString()
  bankName: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsIBAN({ message: i18nValidationMessage("validation:INVALID_IBAN") })
  @IsString()
  iban: string;

  @IsISO31661Alpha2({
    message: i18nValidationMessage("validation:COUNTRY_FORMAT"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  country: string;

  @IsISO31661Alpha2({
    message: i18nValidationMessage("validation:COUNTRY_FORMAT"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  bankCountry: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsString()
  state: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsString()
  city: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsString()
  @IsPostalCode("US", {
    message: i18nValidationMessage("validation:INVALID_ZIP"),
  })
  postalCodeUs: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsString()
  postalCode: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsString()
  streetLine1: string;
}

export class AccountFees {
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  ach: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  swift: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  swiftIn: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  domestic: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  domesticIn: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  processingFee: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  conversionFee: string;
}

export class AccountInformationRequest {
  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  accountNumber: string;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  clabe?: string;

  @Length(9, 9, {
    message: i18nValidationMessage("validation:INVALID_ROUTINGNUMBER"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  routingNumber: string;

  @IsOptional()
  @IsEnum(CounterPartyAccountFIATUSType, {
    message: i18nValidationMessage("validation:INVALID_ENUM"),
  })
  @Transform(({ value }) => (value === "" ? null : value))
  type?: CounterPartyAccountFIATUSType;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsIBAN({ message: i18nValidationMessage("validation:INVALID_IBAN") })
  iban: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsBIC({ message: i18nValidationMessage("validation:INVALID_SWIFTBIC") })
  swiftBic: string;

  @IsString()
  @MaxLength(35)
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  institutionName?: string;

  @IsOptional()
  @IsString()
  description?: string;

  @IsOptional()
  @Type(() => CounterpartyAddressRequest)
  @ValidateNested()
  @Transform(({ value }) => (value === "" ? null : value))
  institutionAddress?: CounterpartyAddressRequest;
}

export class CounterPartyIntermediaryRequest {
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsEnum(CounterPartyIntermediaryIntermediaryType, {
    message: i18nValidationMessage("validation:INVALID_ENUM"),
  })
  intermediaryType: CounterPartyIntermediaryIntermediaryType;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsString()
  institutionName: string;

  @IsOptional()
  @IsEnum(CounterpartyAddressRequest, {
    message: i18nValidationMessage("validation:INVALID_ENUM"),
  })
  @Transform(({ value }) => (value === "" ? null : value))
  institutionAddress?: CounterpartyAddressRequest;

  @IsOptional()
  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @Transform(({ value }) => (value === "" ? null : value))
  routingNumber: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsString()
  swiftBic: string;
}

export class CryptoProfileRequest {
  @IsEnum(ApplicationStateApplicationType, {
    message: i18nValidationMessage("validation:INVALID_ENUM"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  profileType: ApplicationStateApplicationType;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @Matches(/^\S+(\s+\S+)+$/, {
    message: i18nValidationMessage("validation:FULL_NAME_REQUIRED"),
  })
  name: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsEnum(CounterPartyProfileFIATUSRelationshipToCustomer, {
    message: i18nValidationMessage("validation:INVALID_ENUM"),
  })
  relationshipToCustomer: CounterPartyProfileFIATUSRelationshipToCustomer;

  @IsISO31661Alpha2({
    message: i18nValidationMessage("validation:COUNTRY_FORMAT"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  countryCode: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsEnum(CounterPartyAccountCRYPTOWalletType, {
    message: i18nValidationMessage("validation:INVALID_ENUM"),
  })
  walletType: CounterPartyAccountCRYPTOWalletType;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsString()
  asset: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsString()
  network: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsString()
  blockchainAddress: string;
}

export class RegisterCounterpartyRequest {
  @IsEnum(WithdrawalType, {
    message: i18nValidationMessage("validation:INVALID_ENUM"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  withdrawalType: string;

  @ValidateIf((object) => object.withdrawalType === "FIAT")
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsISO31661Alpha2({
    message: i18nValidationMessage("validation:COUNTRY_FORMAT"),
  })
  countryCode: string;
}

export class ProfileRequest {
  @IsEnum(ApplicationStateApplicationType, {
    message: i18nValidationMessage("validation:INVALID_ENUM"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  profileType: ApplicationStateApplicationType;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  name: string;

  @IsOptional()
  @IsEmail({}, { message: i18nValidationMessage("validation:INVALID_EMAIL") })
  @Transform(({ value }) => (value === "" ? null : value))
  email?: string;

  @IsOptional()
  @IsPhoneNumber(undefined, {
    message: i18nValidationMessage("validation:INVALID_PHONE_NUMBER"),
  })
  @Transform(({ value }) => (value === "" ? null : value))
  phoneNumber?: string;

  @IsOptional()
  @IsString({})
  @Transform(({ value }) => (value === "" ? null : value))
  taxReferenceNumber?: string;

  @IsOptional()
  @IsEnum(CounterPartyProfileFIATUSLineOfBusiness, {
    message: i18nValidationMessage("validation:INVALID_ENUM"),
  })
  @Transform(({ value }) => (value === "" ? null : value))
  lineOfBusiness?: CounterPartyProfileFIATUSLineOfBusiness;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsEnum(CounterPartyProfileFIATUSRelationshipToCustomer, {
    message: i18nValidationMessage("validation:INVALID_ENUM"),
  })
  relationshipToCustomer: CounterPartyProfileFIATUSRelationshipToCustomer;

  @IsString()
  @MaxLength(35)
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  addressLine1?: string;

  @IsString()
  @MaxLength(21)
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  city?: string;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  state?: string;

  @IsISO31661Alpha2({
    message: i18nValidationMessage("validation:COUNTRY_FORMAT"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  countryCode: string;

  @ValidateIf((object) => object.countryCode !== "US")
  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  postalCode?: string;

  @ValidateIf((object) => object.countryCode === "US")
  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsPostalCode("US", {
    message: i18nValidationMessage("validation:INVALID_ZIP"),
  })
  postalCodeUs?: string;
}

export class CreateCounterPartyRequest {
  @IsEnum(CounterPartyCounterpartyType, {
    message: i18nValidationMessage("validation:INVALID_ENUM"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  counterpartyType: CounterPartyCounterpartyType;

  @IsEnum(SupportedRails, {
    each: true,
    message: i18nValidationMessage("validation:INVALID_ENUM"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  rails: SupportedRails[];

  @Type(() => ProfileRequest)
  @ValidateNested()
  profile: ProfileRequest;

  @Type(() => CounterPartyIntermediaryRequest)
  @ValidateNested()
  intermediaryInformation: CounterPartyIntermediaryRequest;
}

export class WithdrawalRequest {
  @IsEnum(SupportedRails, {
    each: true,
    message: i18nValidationMessage("validation:INVALID_ENUM"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  withdrawalRail: SupportedRails;

  @IsOptional()
  @IsString()
  @Transform(({ value }) => (value === "" ? null : value))
  description: string;

  @IsUUID("4", { message: i18nValidationMessage("validation:INVALID_UUID") })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  sourceAccountId: string;

  @IsEnum(WithdrawalPurpose, {
    message: i18nValidationMessage("validation:INVALID_ENUM"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  purpose: WithdrawalPurpose;

  @IsNumber()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  amount: number;

  @IsUUID("4", { message: i18nValidationMessage("validation:INVALID_UUID") })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  destinationCounterpartyId: string;

  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsString()
  @MaxLength(50)
  memo: string;
}
